import { useNavigate } from '@tanstack/react-router'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '#components/Button'
import { Icon } from '#components/Icon'
import { Tooltip } from '#components/Tooltip'
import { useAuth } from '#hooks/useAuth'
import { useFetchAnnouncements } from '#hooks/useFetchAnnouncements'
import { getHighestRole, ROLE_SUPER_ADMIN } from '#interfaces/auth'

import styles from './styles.module.scss'

export const AnnouncementIndicator = (): React.JSX.Element | null => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isSuperAdmin = getHighestRole(user?.roles) === ROLE_SUPER_ADMIN
  const { announcements } = useFetchAnnouncements(isSuperAdmin)

  const liveCount = useMemo(
    (): number => announcements.filter((item) => item.status === 'live').length,
    [announcements],
  )

  const handlePress = useCallback((): void => {
    void navigate({ to: '/settings' })
  }, [navigate])

  if (!isSuperAdmin || liveCount === 0) return null

  return (
    <Tooltip content={t('{{ count }} announcement is live', { count: liveCount })} trigger="element">
      <Button
        variant="ghost"
        className={styles.indicator}
        onPress={handlePress}
        aria-label={t('{{ count }} announcement is live', { count: liveCount })}
        data-testid="announcement-indicator"
      >
        <Icon name="megaphone" width="18" height="18" />
        <span className={styles.badge}>{liveCount}</span>
      </Button>
    </Tooltip>
  )
}
